import { useState, useRef, useCallback, UIEvent } from 'react';
import { IContact } from '@trii/types/dist/Contacts';
import * as api from '../../../services/api';
import type { Pagination } from '../../../services/api';

const DEFAULT_PAGINATION: Omit<Pagination, 'filter' | 'currentPage'> = {
  format: 'IContact',
  total: 0,
  lastPage: 1,
  perPage: 10,
  order: 'ASC',
  orderColumn: 'Id',
};

type ApiContext = {
  baseUrl: string;
  spaceId: string;
};

export function useLinkContactsSearch(apiContext: ApiContext | null) {
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  const [contactList, setContactList] = useState<IContact[]>([]);
  const [hasSearched, setHasSearched] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingTimer, setIsLoadingTimer] = useState(false);
  const [isNextPage, setIsNextPage] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [allLoaded, setAllLoaded] = useState(false);

  const resetSearchState = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;

    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }

    setHasSearched(false);
    setAllLoaded(false);
    setCurrentPage(1);
    setIsNextPage(false);
    setContactList([]);
  }, []);

  const mapContacts = (contacts: Array<{ id: string; name: string; phone?: string }>) => {
    return contacts.filter((c) => Boolean(c?.id)) as IContact[];
  };

  const fetchPage = useCallback(
    async ({ query, page }: { query: string; page: number }) => {
      if (!apiContext) return;

      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      setIsLoading(true);
      try {
        const pagination: Pagination = {
          ...DEFAULT_PAGINATION,
          currentPage: page,
          filter: [
            {
              value: query,
              column: 'name',
              condition: '',
            },
          ],
        };

        const response = await api.fetchBusinessTableData({
          baseUrl: apiContext.baseUrl,
          spaceId: apiContext.spaceId,
          pagination,
          signal: controller.signal,
        });

        if (controller.signal.aborted) return;

        const next = mapContacts((response?.contactos ?? []) as any);
        setHasSearched(true);

        const total = response?.paginacion?.total ?? 0;

        setContactList((prev) => {
          const merged =
            page === 1
              ? next
              : (() => {
                  const byId = new Map<string, IContact>();
                  for (const item of prev) byId.set(item.id, item);
                  for (const item of next) byId.set(item.id, item);
                  return Array.from(byId.values());
                })();

          if (total && merged.length >= total) {
            setAllLoaded(true);
          }

          return merged;
        });
      } finally {
        setIsLoading(false);
        setIsLoadingTimer(false);
        setIsNextPage(false);
      }
    },
    [apiContext],
  );

  const handleQueryChanged = useCallback(
    (value: string) => {
      resetSearchState();

      if (!value) return;

      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }

      setIsLoadingTimer(true);

      timerRef.current = setTimeout(() => {
        void fetchPage({ query: value, page: 1 });
      }, 800);
    },
    [fetchPage, resetSearchState],
  );

  const handleScroll = useCallback(
    (e: UIEvent<HTMLElement>, currentSearch: string) => {
      const element = e.target as HTMLElement;
      const bottom = element.scrollHeight - element.scrollTop - element.clientHeight < 1;

      if (!bottom) return;
      if (allLoaded) return;
      if (isLoading) return;
      if (!currentSearch) return;

      const nextPage = currentPage + 1;
      setIsNextPage(true);
      setCurrentPage(nextPage);
      void fetchPage({ query: currentSearch, page: nextPage });
    },
    [allLoaded, isLoading, currentPage, fetchPage],
  );

  return {
    contactList,
    hasSearched,
    isLoading,
    isLoadingTimer,
    isNextPage,
    handleQueryChanged,
    handleScroll,
    resetSearchState,
  };
}
